import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/spinner";
import { ErrorState } from "@/components/error-state";
import { AUTH_KEYS } from "@/api/users";
import { useCheckInvite, useRedeemInvite } from "@/api/orval";
import logo from "@/assets/logo.png";

export const Join = () => {
  const { code = "" } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: validity, isLoading: isCheckingCode } = useCheckInvite(
    { code },
    { query: { enabled: !!code, retry: false } },
  );
  const redeem = useRedeemInvite();

  const [codeInput, setCodeInput] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const { access_token } = await redeem.mutateAsync({
        data: { code, email, username, password },
      });
      localStorage.setItem("access_token", access_token);
      await queryClient.refetchQueries({ queryKey: AUTH_KEYS.user });
      navigate("/onboarding");
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ??
          "Something went wrong. Please try again.",
      );
    }
  };

  // Reached /join without a code: ask for one, then continue to /join/:code.
  if (!code) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <div className="flex items-center gap-2 self-center font-medium">
            <img
              src={logo}
              alt=""
              className="h-9 w-9 rounded-lg object-contain"
            />
            everystreet.run
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Enter your invite code</CardTitle>
              <CardDescription>
                everystreet.run is invite-only for now.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const trimmed = codeInput.trim();
                  if (trimmed) navigate(`/join/${encodeURIComponent(trimmed)}`);
                }}
                className="grid gap-4"
              >
                <div className="grid gap-2">
                  <Label htmlFor="code">Invite code</Label>
                  <Input
                    id="code"
                    placeholder="e.g. SUMMER25"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!codeInput.trim()}
                >
                  Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isCheckingCode) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <div className="w-[200px]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!validity?.valid) {
    return (
      <ErrorState
        title="This invite isn't valid"
        description="This invite code is invalid or has already been used. Ask whoever invited you for a fresh link."
        fullScreen
      />
    );
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex items-center gap-2 self-center font-medium">
          <img
            src={logo}
            alt=""
            className="h-9 w-9 rounded-lg object-contain"
          />
          everystreet.run
        </div>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Create your account</CardTitle>
            <CardDescription>
              You&apos;re invited. Set up your account to get started.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="m@example.com"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    placeholder="Nickname or first name"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={redeem.isPending}
                >
                  {redeem.isPending ? "Creating account..." : "Create account"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
